import { Matrix4, Vector3 } from 'three'
import { ICE_BREAK_VFX, ICE_SHARD_MATERIAL } from '../game/tuning'
import { curveAt, gradientAt } from './unityCurve'

/**
 * 📐 `ice_vfx` — the shower of shards when the last ice chip goes.
 *
 * `Bus.BreakIce` (`__0x20fb820.c`:91-95) plays it, once, and the whole system
 * is read out of the prefab in `ICE_BREAK_VFX` — see there for what came from
 * where. This file is the simulation half: where the shards start, where they
 * go, what size and colour they are along the way, and when they stop
 * existing. Nothing here draws.
 *
 * The original is a Unity `ParticleSystem`; this is not a port of Unity's
 * solver, only of the modules that system actually switches on. What it uses:
 *
 *  - **Emission**: one burst of `burstCount`, `rateOverTime: 0`. No stream.
 *  - **Shape**: `type: 13` MeshRenderer, `placementMode: 2` Triangle — a shard
 *    is born on a triangle of `Ice/Fake`'s mesh and leaves along that
 *    triangle's normal, since `randomDirectionAmount: 0`.
 *  - **Initial**: `startLifetime`, `startSpeed`, `startSize`, `startRotation`
 *    (Z only — `rotation3D: 0`), each a RandomBetweenTwoConstants pair;
 *    `gravityModifier` a constant 1.
 *  - **Size over lifetime**: one curve, `startSize × curve(age / lifetime)`.
 *  - **Colour over lifetime**: one gradient, colour and alpha keys separately.
 *  - **Velocity over lifetime**: `radial` 0.5 away from the system's origin,
 *    and `speedModifier` 2 on the whole motion.
 *  - **Collision**: world, against the `Ground` layer — the plane y = 0.
 *
 * ⚠️ This file used to say "everything else is at its default: no colour-over-
 * lifetime, no size-over-lifetime, no collision", and the prefab says the
 * opposite for all three (FRZ-2). Only `RotationModule` is really off.
 *
 * ⚠️ **`gravityModifier` is a multiplier on `Physics.gravity`, not an
 * acceleration.** Unity's default is (0, -9.81, 0). Reading the number as m/s²
 * would make the whole burst hang in the air.
 *
 * ⚠️ Determinism: the original calls `Random.Range`. Nothing here does — every
 * per-shard number comes out of `hash`, seeded by the bus id and the shard
 * index, so a test that fires the same burst twice gets the same shards
 * (CLAUDE.md §4).
 */

/** Unity's default `Physics.gravity.y`. `gravityModifier` scales this. */
export const UNITY_GRAVITY = -9.81

export interface Shard {
  position: Vector3
  /**
   * The particle's own velocity: `startSpeed` along the birth normal, plus
   * whatever gravity and contact have done to it since. What the shard moves
   * by per second is this **plus** the radial term, **times** `speedModifier`
   * — see `advanceIceBurst`.
   */
  velocity: Vector3
  /** `startLifetime`, fixed at birth. */
  lifetime: number
  /** Seconds it has left. Born at `lifetime`, dead at 0. */
  remaining: number
  /** `startSize`, fixed at birth. */
  startSize: number
  /** `startSize × sizeOverLifetime(age / lifetime)` — what to draw it at now. */
  size: number
  /** `startRotation`, radians about world Z. `RotationModule` is off, so it never changes. */
  rotation: number
  /** Colour-over-lifetime, RGB in [0, 1], for the current age. */
  tint: [number, number, number]
  /** Colour-over-lifetime's alpha for the current age. */
  alpha: number
  /** `gravityModifier`, the multiplier on `UNITY_GRAVITY`. */
  gravity: number
  /**
   * The system's origin the `radial` term pushes away from — the `ice_vfx`
   * transform, 4.36 above the root of the bus that fired this shard. Shared by
   * every shard of one burst, and bursts from different buses have different
   * ones, which is why it lives on the shard and not on the step.
   */
  origin: Vector3
  /** Which of the four shard meshes this one draws, 0..3, equally weighted. */
  mesh: number
}

/**
 * A deterministic stand-in for `Random.Range`, in [0, 1).
 *
 * The same integer hash the port uses elsewhere for per-character jitter — see
 * `characterAnim.ts`. Two different call sites must pass different `salt`, or
 * they draw the same number and the burst comes out in lockstep.
 */
export function hash01(seed: number, salt: number): number {
  let h = (seed * 0x9e3779b1) ^ (salt * 0x85ebca6b)
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d)
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39)
  h ^= h >>> 15
  return (h >>> 0) / 4294967296
}

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

/**
 * `SizeModule.curve` at a normalised age, evaluated the way Unity evaluates an
 * `AnimationCurve`: cubic Hermite between neighbouring keys using the stored
 * `outSlope` of the left key and `inSlope` of the right, and clamped to the
 * end keys outside [first, last].
 *
 * With this prefab's slopes the tail segment is exactly linear (see
 * `ICE_BREAK_VFX.sizeOverLifetime`), but the evaluator does not assume that.
 */
export function sizeOverLifetime(t: number): number {
  // The evaluator moved to `unityCurve.ts` when the countdown puff needed the
  // same one (FRZ-7); the closed-form check in `iceBurst.test.ts` still runs
  // through here.
  return curveAt(ICE_BREAK_VFX.sizeOverLifetime, t)
}

/**
 * `ColorModule.gradient` at a normalised age. Blend mode (`m_Mode: 0`): linear
 * between neighbouring keys, held flat beyond the first and last. Colour keys
 * and alpha keys are separate lists with separate times, as Unity stores them.
 */
export function colourOverLifetime(t: number): { rgb: [number, number, number]; alpha: number } {
  const { colour, alpha } = ICE_BREAK_VFX.colourOverLifetime
  return gradientAt(colour, alpha, t)
}

const WHITE: readonly [number, number, number] = [1, 1, 1]

/**
 * What the pool actually draws a shard with: the `ColorModule` tint and alpha
 * when `Ice 1.mat`'s shader is taken to multiply the particle colour in, and
 * plain white at full alpha otherwise — the material's own `_Color`, opaque,
 * is then the whole colour. ❓ `ICE_SHARD_MATERIAL.usesParticleColour` says
 * which; the gradient is evaluated either way so the shard carries it.
 */
export function shardDrawColour(s: Shard): { tint: readonly [number, number, number]; alpha: number } {
  return ICE_SHARD_MATERIAL.usesParticleColour ? { tint: s.tint, alpha: s.alpha } : { tint: WHITE, alpha: 1 }
}

/** Write the age-dependent fields for `remaining` seconds left of `lifetime`. */
function refreshOverLifetime(s: Shard): void {
  const t = s.lifetime > 0 ? 1 - s.remaining / s.lifetime : 1
  s.size = s.startSize * sizeOverLifetime(t)
  const c = colourOverLifetime(t)
  s.tint = c.rgb
  s.alpha = c.alpha
}

/**
 * Fire the burst.
 *
 * `triangles` are the emitter's triangles in the space the shards will live in
 * — each entry is three corners and the outward normal, which is what
 * `placementMode: 2` needs: a point uniformly inside a triangle, moving along
 * its normal.
 *
 * `origin` is the system's own position in that space — the `ice_vfx`
 * transform, 4.36 above the bus root. The radial velocity term points away
 * from it. Left out, it is the space's origin, which is only right for a
 * burst fired at the world origin.
 */
export function emitIceBurst(
  triangles: readonly { a: Vector3; b: Vector3; c: Vector3; normal: Vector3 }[],
  seed: number,
  count: number = ICE_BREAK_VFX.burstCount,
  origin: Vector3 = new Vector3(),
): Shard[] {
  const out: Shard[] = []
  if (triangles.length === 0) return out

  for (let i = 0; i < count; i++) {
    const tri = triangles[Math.floor(hash01(seed, i * 9 + 1) * triangles.length) % triangles.length]

    // Uniform inside a triangle: the sqrt keeps the corner from being denser
    // than the middle, which a plain (u, v) pair would make it.
    let u = hash01(seed, i * 9 + 2)
    let v = hash01(seed, i * 9 + 3)
    const su = Math.sqrt(u)
    u = 1 - su
    v = v * su
    const w = 1 - u - v

    const position = new Vector3(
      tri.a.x * u + tri.b.x * v + tri.c.x * w,
      tri.a.y * u + tri.b.y * v + tri.c.y * w,
      tri.a.z * u + tri.b.z * v + tri.c.z * w,
    )

    const speed = lerp(ICE_BREAK_VFX.speed[0], ICE_BREAK_VFX.speed[1], hash01(seed, i * 9 + 4))
    const lifetime = lerp(
      ICE_BREAK_VFX.lifetimeSeconds[0],
      ICE_BREAK_VFX.lifetimeSeconds[1],
      hash01(seed, i * 9 + 5),
    )
    const startSize = lerp(ICE_BREAK_VFX.size[0], ICE_BREAK_VFX.size[1], hash01(seed, i * 9 + 6))
    const shard: Shard = {
      position,
      // `randomDirectionAmount: 0` — straight out along the surface normal.
      velocity: tri.normal.clone().multiplyScalar(speed),
      lifetime,
      remaining: lifetime,
      startSize,
      size: startSize,
      rotation: lerp(
        ICE_BREAK_VFX.startRotation[0],
        ICE_BREAK_VFX.startRotation[1],
        hash01(seed, i * 9 + 7),
      ),
      tint: [1, 1, 1],
      alpha: 1,
      // A constant in the prefab (`minMaxState: 0`), so no draw for it.
      gravity: ICE_BREAK_VFX.gravityModifier,
      origin,
      // Four meshes, `m_MeshWeighting` 1 apiece.
      mesh: Math.floor(hash01(seed, i * 9 + 8) * ICE_BREAK_VFX.meshes.length) % 4,
    }
    refreshOverLifetime(shard)
    out.push(shard)
  }
  return out
}

const _radial = new Vector3()
const _move = new Vector3()

/**
 * Step every shard and drop the dead ones. Returns the survivors, in place.
 *
 * Per shard, in this order:
 *
 *  1. `velocity.y += gravity × dt` — semi-implicit Euler, gravity on the
 *     velocity first and the new velocity moves the shard. Over a 1–1.5 s life
 *     at 60 Hz the difference from anything fancier is well under a shard's
 *     own width.
 *  2. The motion is `(velocity + radial × dir) × speedModifier`, `dir` the unit
 *     vector from the shard's `origin` to the shard. `radial` adds to the motion without
 *     being stored in `velocity` — it is a velocity-over-lifetime term, not
 *     something contact can reflect.
 *  3. Contact with the ground plane: when the shard would end the step below
 *     `groundY + radius`, it is put on the plane and its own `velocity` is
 *     split at the plane's normal — the normal part reflected and scaled by
 *     `bounce`, the tangential part scaled by `1 − dampen`. ❓ That split is
 *     the port's reading of "Bounce: fraction of speed that rebounds" and
 *     "Dampen: fraction of speed lost"; Unity's composition is native code the
 *     kit does not carry. `radius` is `size / 2 × radiusScale` — at 0.01 that is
 *     at most 0.024 u, so the exact rule for it cannot be seen.
 *  4. `size`, `tint`, `alpha` for the new age.
 */
export function advanceIceBurst(
  shards: Shard[],
  dt: number,
  groundY: number = ICE_BREAK_VFX.collision.groundY,
): Shard[] {
  if (dt <= 0) return shards
  const { radial, speedModifier } = ICE_BREAK_VFX.velocityOverLifetime
  const { dampen, bounce, radiusScale } = ICE_BREAK_VFX.collision
  let write = 0
  for (let i = 0; i < shards.length; i++) {
    const s = shards[i]
    s.remaining -= dt
    if (s.remaining <= 0) continue

    s.velocity.y += UNITY_GRAVITY * s.gravity * dt

    _radial.subVectors(s.position, s.origin)
    if (_radial.lengthSq() > 1e-12) _radial.normalize()
    else _radial.set(0, 0, 0)
    _move.copy(s.velocity).addScaledVector(_radial, radial).multiplyScalar(speedModifier)
    s.position.addScaledVector(_move, dt)

    const radius = s.size * 0.5 * radiusScale
    if (s.position.y < groundY + radius && _move.y < 0) {
      s.position.y = groundY + radius
      // Split at the plane normal (0, 1, 0): reflect and scale the normal
      // part, damp the tangential part.
      const vn = s.velocity.y
      s.velocity.x *= 1 - dampen
      s.velocity.z *= 1 - dampen
      s.velocity.y = -vn * bounce
    }

    refreshOverLifetime(s)
    shards[write++] = s
  }
  shards.length = write
  return shards
}

/**
 * The emitter's triangles, in world space.
 *
 * `ShapeModule.type: 13` samples a **MeshRenderer**, and the one it names is
 * `Ice/Fake`'s — the node carrying `Bus_N_single`, the whole bus in one piece,
 * with its renderer switched off precisely so it can be a shape and not a
 * drawing (IC-6). So the shards come off the surface of the bus itself.
 *
 * ❓ `Ice/Fake` sits at scale 0.90277, yaw 180°, offset (0, 1.99, −1.63) inside
 * `Ice`. The port emits from the bus's own transform instead of reproducing
 * that, so shards start on the body rather than on a shell 10 % smaller than
 * it. Close, not exact, and the difference is under a shard's own size.
 */
export function emitterTriangles(
  geometry: { getAttribute(name: string): { count: number; getX(i: number): number; getY(i: number): number; getZ(i: number): number } | undefined; getIndex(): { count: number; getX(i: number): number } | null },
  matrix: Matrix4,
  /** Every `stride`-th triangle. The emitter has thousands; the burst has 60. */
  stride = 1,
): { a: Vector3; b: Vector3; c: Vector3; normal: Vector3 }[] {
  const position = geometry.getAttribute('position')
  if (!position) return []
  const index = geometry.getIndex()
  const count = index ? index.count : position.count
  const at = (i: number): number => (index ? index.getX(i) : i)

  const out: { a: Vector3; b: Vector3; c: Vector3; normal: Vector3 }[] = []
  const step = Math.max(1, Math.floor(stride)) * 3
  for (let i = 0; i + 2 < count; i += step) {
    const a = vertexAt(position, at(i)).applyMatrix4(matrix)
    const b = vertexAt(position, at(i + 1)).applyMatrix4(matrix)
    const c = vertexAt(position, at(i + 2)).applyMatrix4(matrix)
    const normal = new Vector3()
      .subVectors(b, a)
      .cross(new Vector3().subVectors(c, a))
    // A degenerate triangle has no direction to leave along; skip rather than
    // emit a shard that never moves.
    if (normal.lengthSq() < 1e-12) continue
    out.push({ a, b, c, normal: normal.normalize() })
  }
  return out
}

function vertexAt(
  position: { getX(i: number): number; getY(i: number): number; getZ(i: number): number },
  i: number,
): Vector3 {
  return new Vector3(position.getX(i), position.getY(i), position.getZ(i))
}
